<template>
  <teleport to='body'>
    <Transition :duration='500'>
      <div
        v-show='isActive'
        ref='modalRef'
        class='b-modal v4'
        :class='cssClass'
      >
        <div class='modal-shade' />
        <div
          class='modal-inner'
          :class='{ "is-mobile-handle": isMobileHandle }'
        >
          <div v-if='isMobileHandle' class='mobile-handle' />
          <button v-else class='modal-close'><CloseIcon /></button>
          <div
            class='content-wrapper'
            data-scroll-lock-scrollable
          >
            <slot v-if='isInitialized' />
          </div>
        </div>
      </div>
    </Transition>
  </teleport>
</template>

<script>
export default {
  name: 'Modal'
};
</script>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick
} from 'vue';

import { useMobileDetect } from '../composables/mobile_detect';
import CloseIcon from '@/../images/vue/components/modal/x-close.svg?inline';

import {
  disablePageScroll,
  enablePageScroll,
  addScrollableSelector,
  removeScrollableSelector
} from 'scroll-lock';
import showModal from '@/utils/show_modal';

const props = defineProps({
  isActive: { type: Boolean, required: true },
  isSlideDownHandle: { type: Boolean, required: true },
  cssClass: { type: String, required: false, default: undefined }
});
const emit = defineEmits(['modal:show', 'modal:hide']);

const MOBILE_HANDLE_SWIPE_THRESHOLD = 40;

const { isMobile } = useMobileDetect();
const modalRef = ref(null);
const isInitialized = ref(false);
const isMobileHandle = computed(() => isMobile.value && props.isSlideDownHandle);

let modal = null;
let swipeListener = null;

onMounted(() => {
  if (props.isActive) {
    setupModal();
    modal.show();
  }
});

watch(() => props.isActive, (newValue, oldValue) => {
  if (newValue && !modal) {
    nextTick().then(() => {
      setupModal();
      modal.show();
    });
    return;
  }

  if (newValue && !oldValue) {
    modal.show();
  } else if (!newValue && oldValue) {
    modal.hide();
    modal.destroy();
    modal = null;
  }
});

function setupModal() {
  isInitialized.value = true;
  const $modal = $(modalRef.value);

  modal = showModal({
    $modal,
    show() {
      addScrollableSelector('[data-scroll-lock-scrollable]');
      disablePageScroll();

      if (isMobileHandle.value) {
        setupSwipeListener();
      }

      $modal.addClass('is-active');
      emit('modal:show');
    },
    hide() {
      removeScrollableSelector('[data-scroll-lock-scrollable]');
      enablePageScroll();

      if (swipeListener) {
        swipeListener.off();
        swipeListener = null;
      }

      $modal.removeClass('is-active');
      emit('modal:hide');
    },
    isHiddenCheck: () => !$modal.hasClass('is-active')
  });

  $modal.find('.modal-shade').on('click', () => modal.hide());
  $modal.find('.modal-close').on('click', () => modal.hide());
}

async function setupSwipeListener() {
  if (!modalRef.value) return;

  const { default: SwipeListener } = await import('swipe-listener');
  const mobileHandleNode = modalRef.value.querySelector('.mobile-handle');

  if (!mobileHandleNode) return;

  swipeListener = SwipeListener(mobileHandleNode);

  mobileHandleNode.addEventListener('swipe', e => {
    if (e.detail.directions.bottom &&
      e.detail.y[1] - e.detail.y[0] > MOBILE_HANDLE_SWIPE_THRESHOLD
    ) {
      modal.hide();
    }
  });
}
</script>

<style scoped lang="sass">
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'

.b-modal
  +lte_ipad
    --modal-max-height: calc(var(--vh, 1vh)*87)
    --modal-padding: #{rem4(24px)}

  +gte_laptop
    --modal-max-height: max(790px, 80vh)
    --modal-padding: 24px

  & > .modal-inner
    padding: var(--modal-padding) var(--modal-padding) 0 var(--modal-padding)

    +lte_ipad
      border-radius: rem4(16px) rem4(16px) 0 0
      bottom: 0
      left: 0
      max-height: var(--modal-max-height)
      overflow: hidden
      right: 0
      top: auto
      transition: max-height var(--transition-duration), transform var(--transition-duration)

    +gte_laptop
      width: 500px

    &.is-mobile-handle
      padding-top: 0

      & > .modal-close
        +lte_ipad
          display: none

    & > .modal-close
      +lte_ipad
        height: rem4(20px)
        right: var(--modal-padding)
        top: calc(var(--modal-padding) + #{rem4(5px)})
        width: rem4(20px)

      +gte_laptop
        background: #fff
        border-radius: 100%
        height: 36px
        padding: 8px
        right: #{-29px - 36px}
        top: 0
        width: 36px

    .content-wrapper
      overflow-y: scroll
      padding-bottom: var(--modal-padding)
      position: relative

      +lte_ipad
        max-height: calc(var(--modal-max-height) - rem4(44px))
        transition: max-height var(--transition-duration)

      +gte_laptop
        max-height: var(--modal-max-height)

  +lte_ipad
    &:not(.is-active) > .modal-inner
      transform: translateY(100%)
      display: block !important

    &.is-active > .modal-inner
      transform: translateY(0)

    .mobile-handle
      background: #fff
      display: flex
      justify-content: center
      padding-bottom: rem4(22px)
      padding-top: rem4(22px)

      +gte_laptop
        display: none

      &:before
        content: ''
        background: var(--gray-300)
        border-radius: rem4(4px)
        height: rem4(4px)
        width: rem4(40px)
</style>
